import { useState, useEffect } from 'react'
import { erpApi } from '../../api/erp'
import { fmtMonto } from '../../utils/fecha'
import { colors, font, text, space, radius, shadow, sx } from '../../design/tokens'

// ---------------------------------------------------------------- helpers

/** uuid del formulario: nace al abrirlo y sobrevive a los reintentos (Idempotency-Key). */
export const nuevoUuid = () => (globalThis.crypto?.randomUUID
  ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  }))

export const fmtImporte = (n) => '$' + fmtMonto(n)

/** Mensaje para el operador a partir de un error de axios (ERP o validación de LGR). */
export function mensajeError(e) {
  const d = e?.response?.data
  if (!d) return e?.message || 'No se pudo completar la operación.'
  if (d.message && d.errors && typeof d.errors === 'object') {
    const primero = Object.values(d.errors).flat()[0]
    return primero ? `${d.message} ${primero}` : d.message
  }
  return d.message || 'No se pudo completar la operación.'
}

export const TIPO_LABEL = { factura: 'Factura', remito: 'Remito', nota_credito: 'Nota de crédito', recibo: 'Recibo' }

export const OPERACION_LABEL = {
  'ventas/facturas': 'Factura', 'ventas/remitos': 'Remito', 'ventas/recibos': 'Recibo',
  'ventas/notas-credito': 'Nota de crédito', 'ventas/devoluciones': 'Devolución',
  'compras/pedidos': 'Pedido', 'compras/recepciones': 'Recepción', 'compras/facturas': 'Factura proveedor',
  'compras/notas-credito': 'NC proveedor', 'compras/pagos': 'Pago', 'compras/gastos': 'Gasto',
  terceros: 'Alta de tercero',
}

export function labelOperacion(ruta) {
  if (ruta.endsWith('/anular')) return 'Anulación'
  return OPERACION_LABEL[ruta] || ruta
}

export function totalRenglones(renglones, campo = 'precio') {
  return renglones.reduce((acc, r) => acc + (parseFloat(r.cantidad) || 0) * (parseFloat(r[campo]) || 0), 0)
}

// ---------------------------------------------------------------- hooks / puras

export function useDepositos() {
  const [depositos, setDepositos] = useState([])
  useEffect(() => {
    erpApi.depositos().then(({ data }) => setDepositos(data.depositos || [])).catch(() => {})
  }, [])
  return depositos
}

export function etiquetaTercero(t) {
  if (!t) return ''
  return [t.codigo, t.razon_social].filter(Boolean).join(' · ')
}

export function renglonesValidos(renglones, campo = 'precio') {
  return renglones.length > 0 && renglones.every(r =>
    (r.sku || r.descripcion) && parseFloat(r.cantidad) > 0 && r[campo] !== '' && !isNaN(parseFloat(r[campo])))
}

/** Renglones listos para el ERP: sin descripción de los que tienen sku, sin vacíos. */
export function renglonesPayload(renglones, campo = 'precio') {
  return renglones.map(r => r.sku
    ? { sku: r.sku, cantidad: r.cantidad, [campo]: r[campo] }
    : { descripcion: r.descripcion, cantidad: r.cantidad, [campo]: r[campo] })
}

export function tonoEstado(estado) {
  if (!estado) return 'neutro'
  if (['anulado', 'anulada'].includes(estado)) return 'err'
  if (['emitido', 'emitida', 'confirmado', 'cerrado', 'ok'].includes(estado)) return 'ok'
  return 'neutro'
}


/** Hover de fila clickeable — mismo patrón que PagosPage (fondo bgSoft). */
export const hoverFila = {
  onMouseEnter: e => { e.currentTarget.style.background = colors.bgSoft },
  onMouseLeave: e => { e.currentTarget.style.background = '' },
}

// ---------------------------------------------------------------- estilos compartidos

export const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl, gap: space.md, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `-${space.md}px 0 ${space.lg}px`, color: colors.textSec, fontSize: text.sec },
  acciones: { display: 'flex', gap: space.sm, flexWrap: 'wrap' },
  filtros: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  input: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', background: colors.bg, boxSizing: 'border-box', width: '100%' },
  inputCelda: { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  textarea: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', minHeight: 56, resize: 'vertical' },
  campo: { display: 'flex', flexDirection: 'column', gap: space.xs, fontSize: text.body },
  check: { display: 'flex', alignItems: 'center', gap: space.sm, fontSize: text.body, cursor: 'pointer' },
  label: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: space.md },
  seccion: { display: 'flex', flexDirection: 'column', gap: space.md },
  pie: { display: 'flex', justifyContent: 'flex-end', gap: space.sm, marginTop: space.lg },
  info: { margin: `0 0 ${space.md}px`, color: colors.textSec, fontSize: text.sec },

  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: `${space.md}px ${space.xl}px`, textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44 },
  trClick: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44, cursor: 'pointer' },
  td: { padding: `${space.md}px ${space.xl}px`, verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.25 },
  tdMono: { padding: `${space.md}px ${space.xl}px`, verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdFecha: { padding: `${space.md}px ${space.xl}px`, verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdSec: { padding: `${space.md}px ${space.xl}px`, verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, lineHeight: 1.25 },
  tdImporte: { padding: `${space.md}px ${space.xl}px`, verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right', lineHeight: 1.25 },
  empty: { padding: 32, textAlign: 'center', color: colors.textMeta },
  quitar: { background: 'none', border: 'none', color: colors.textMeta, cursor: 'pointer', fontSize: 18, lineHeight: 1 },

  badge: { ...sx.badge, whiteSpace: 'nowrap' },
  tagWarn: { fontSize: text.meta, color: colors.warn, background: colors.warnBg, padding: '1px 6px', borderRadius: radius.xs, fontWeight: 600, whiteSpace: 'nowrap', textTransform: 'uppercase' },

  chipTercero: { display: 'flex', alignItems: 'center', gap: space.sm, padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.bgSoft, minHeight: 34, boxSizing: 'border-box' },
  chipCodigo: { fontFamily: font.mono, fontSize: text.sec, color: colors.textSec },
  chipNombre: { fontWeight: 500, color: colors.text, flex: 1 },
  chipDoc: { fontFamily: font.mono, fontSize: text.sec, color: colors.textMeta },
  chipQuitar: { background: 'none', border: 'none', color: colors.textSec, cursor: 'pointer', fontSize: 18, lineHeight: 1 },

  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxShadow: shadow.dropdown, zIndex: 100, maxHeight: 260, overflowY: 'auto' },
  ddItem: { padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}` },
  ddFila: { display: 'flex', gap: space.sm, alignItems: 'center' },
  ddCodigo: { fontFamily: font.mono, fontSize: text.sec, color: colors.textSec, minWidth: 64 },
  ddNombre: { fontWeight: 500, color: colors.text, flex: 1 },
  ddMeta: { fontSize: text.meta, color: colors.textMeta, marginTop: 2 },
  ddDoc: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' },
  ddVacio: { padding: 12, color: colors.textMeta, fontSize: text.sec },

  aviso: { background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warn}33`, borderRadius: radius.sm, padding: '8px 12px', fontSize: text.body, fontWeight: 500, marginTop: space.md },
  error: { background: colors.errBg, color: colors.err, border: `1px solid ${colors.err}33`, borderRadius: radius.sm, padding: '8px 12px', fontSize: text.body, marginTop: space.md },
  ok: { background: colors.okBg, color: colors.ok, border: `1px solid ${colors.ok}33`, borderRadius: radius.sm, padding: '10px 12px', fontSize: text.body, marginTop: space.md },
  okTitulo: { fontWeight: 600, marginBottom: space.xs },
  numero: { fontFamily: font.mono, fontWeight: 600 },

  saldoGrande: { fontFamily: font.mono, fontSize: text.display, fontWeight: 600, color: colors.text, fontVariantNumeric: 'tabular-nums' },
  card: { ...sx.card },
  cardTitulo: { margin: `0 0 ${space.md}px`, fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
}
